import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Alert,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

import JalaliDateField from "../Components/JalaliDateField";
const MONTH_NAMES = {
  january: "01",
  february: "02",
  march: "03",
  april: "04",
  may: "05",
  june: "06",
  july: "07",
  august: "08",
  september: "09",
  october: "10",
  november: "11",
  december: "12",
};

const normalizeMonth = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const lowered = text.toLowerCase();
  if (MONTH_NAMES[lowered]) return MONTH_NAMES[lowered];
  const numeric = Number(text);
  if (Number.isInteger(numeric) && numeric >= 1 && numeric <= 12) {
    return String(numeric).padStart(2, "0");
  }
  return lowered;
};

const PayrollEdit = ({ open, onClose, editData, token, onSaved }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleUpdate, loading, isSuccess } = useUpdate("shop/payrolls", token);

  // Fetch foreign key data: shops, employees, and currencies.
  const { data: shopsData, loading: shopLoading } = useFetchObjects(["shop"], "shop/shop/?page_size=1000", token);
  const { data: employeesData, loading: employeeLoading } = useFetchObjects(["employee"], "shop/employees/?page_size=1000", token);
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(["currency"], "shop/currencies?page_size=all", token);

  const shops = useMemo(
    () => (Array.isArray(shopsData) ? shopsData : shopsData?.results || []),
    [shopsData]
  );
  const employees = useMemo(
    () => (Array.isArray(employeesData) ? employeesData : employeesData?.results || []),
    [employeesData]
  );
  const currencies = useMemo(
    () =>
      getUniqueCurrencies(
        Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || []
      ),
    [currenciesData]
  );

  const [selectedShop, setSelectedShop] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [salary, setSalary] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [monthYear, setMonthYear] = useState("");
  const [notes, setNotes] = useState("");
  const [advanceDeduction, setAdvanceDeduction] = useState("");
  const initialRateSelectionRef = useRef("");
  const paymentEditedRef = useRef(false);
  const employeeOptions = useMemo(() => {
    if (!selectedShop) return employees;
    return employees.filter((employee) => {
      const employeeShopId = employee?.shop?.id || employee?.shop || "";
      return String(employeeShopId) === String(selectedShop);
    });
  }, [employees, selectedShop]);
  const safeSelectedShop = selectedShop || "";
  const safeSelectedEmployee = selectedEmployee || "";
  const safeSelectedCurrency = selectedCurrency || "";
  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "payroll-edit", selectedShop],
    selectedShop ? `shop/accounts/?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const accounts = useMemo(
    () => (Array.isArray(accountsData) ? accountsData : accountsData?.results || []),
    [accountsData]
  );
  const selectedAccount = useMemo(
    () => accounts.find((acc) => String(acc.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );
  const sameCurrencyAccounts = useMemo(
    () => accounts.filter((acc) => String(acc.currency) === String(selectedCurrency)),
    [accounts, selectedCurrency]
  );
  const selectedPeriod = useMemo(() => {
    if (!monthYear) return { year: "", month: "" };
    const [year, month] = monthYear.split("-");
    return { year: year || "", month: normalizeMonth(month) };
  }, [monthYear]);
  const { data: advancesData } = useFetchObjects(
    ["advances-for-payroll-edit", selectedEmployee, selectedPeriod.year, selectedPeriod.month],
    selectedEmployee && selectedPeriod.year
      ? `shop/advances/?employee=${selectedEmployee}&year=${selectedPeriod.year}&page_size=1000`
      : "",
    token
  );
  const employeeAdvances = useMemo(
    () => (Array.isArray(advancesData) ? advancesData : advancesData?.results || []),
    [advancesData]
  );
  const { data: payrollsData } = useFetchObjects(
    ["payroll-deductions-edit", selectedEmployee, selectedPeriod.year, selectedPeriod.month],
    selectedEmployee && selectedPeriod.year
      ? `shop/payrolls/?employee=${selectedEmployee}&year=${selectedPeriod.year}&page_size=1000`
      : "",
    token
  );
  const employeePayrolls = useMemo(
    () => (Array.isArray(payrollsData) ? payrollsData : payrollsData?.results || []),
    [payrollsData]
  );
  const monthlyAdvanceTotal = useMemo(
    () =>
      employeeAdvances
        .filter((adv) => normalizeMonth(adv?.month) === selectedPeriod.month)
        .reduce((sum, adv) => sum + Number(adv?.amount || 0), 0),
    [employeeAdvances, selectedPeriod.month]
  );
  const previousPayrollDeductions = useMemo(
    () =>
      employeePayrolls
        .filter(
          (payroll) =>
            String(payroll?.id) !== String(editData?.id) &&
            normalizeMonth(payroll?.month) === selectedPeriod.month
        )
        .reduce((sum, payroll) => sum + Number(payroll?.advance_deduction || 0), 0),
    [employeePayrolls, editData?.id, selectedPeriod.month]
  );
  const previousRemainingAdvance = Math.max(monthlyAdvanceTotal - previousPayrollDeductions, 0);
  const previousAdvance = previousRemainingAdvance;
  const netSalary = Math.max(Number(salary || 0) - Number(advanceDeduction || 0), 0);
  const remainingPaymentBalance = Math.max(netSalary - Number(amount || 0), 0);
  const paymentStatus = remainingPaymentBalance > 0 ? "Pending" : "Completed";

  // Set initial values from editData.
  useEffect(() => {
    if (editData) {
      setSelectedShop(
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : ""
      );
      setSelectedEmployee(
        editData.employee && editData.employee.id
          ? String(editData.employee.id)
          : editData.employee
          ? String(editData.employee)
          : ""
      );
      setSelectedCurrency(
        editData.currency_details && editData.currency_details.id
          ? String(editData.currency_details.id)
          : editData.currency && editData.currency.id
          ? String(editData.currency.id)
          : editData.currency
          ? String(editData.currency)
          : ""
      );
      const accountId = editData.account_details?.id
        ? String(editData.account_details.id)
        : editData.account
          ? String(editData.account)
          : "";
      setSelectedAccountId(accountId);
      setExchangeRate(editData.exchange_rate || "");
      paymentEditedRef.current = true;
      setAmount(editData.amount || "");
      setSalary(editData.employee_details?.salary || "");
      setAdvanceDeduction(editData.advance_deduction || "");
      const year = editData.year || "";
      const month = normalizeMonth(editData.month);
      setMonthYear(year && month ? `${year}-${month}` : "");
      setPaymentDate(editData.payment_date ? editData.payment_date.substring(0, 16) : "");
      setNotes(editData.notes || "");
      const currencyId = editData.currency_details?.id
        ? String(editData.currency_details.id)
        : editData.currency
          ? String(editData.currency)
          : "";
      initialRateSelectionRef.current = `${currencyId}:${accountId}`;
    }
  }, [editData]);

  useEffect(() => {
    if (!selectedEmployee) {
      setSalary("");
      return;
    }
    const employee = employees.find((emp) => String(emp.id) === String(selectedEmployee));
    setSalary(employee?.salary ?? editData?.employee_details?.salary ?? "");
  }, [selectedEmployee, employees, editData]);

  useEffect(() => {
    if (
      selectedEmployee &&
      !employeeOptions.some((employee) => String(employee.id) === String(selectedEmployee))
    ) {
      setSelectedEmployee("");
      setSalary("");
      setAmount("");
    }
  }, [selectedEmployee, employeeOptions]);

  useEffect(() => {
    if (!selectedEmployee) {
      setAdvanceDeduction("");
      return;
    }
    const rawSalary = Number(salary || 0);
    const deduction = Math.min(previousRemainingAdvance || 0, rawSalary || 0);
    setAdvanceDeduction(deduction > 0 ? String(deduction) : "");
  }, [selectedEmployee, salary, previousRemainingAdvance, monthYear]);

  useEffect(() => {
    const rawSalary = Number(salary || 0);
    const deduction = Number(advanceDeduction || 0);
    if (!paymentEditedRef.current && Number.isFinite(rawSalary) && Number.isFinite(deduction)) {
      setAmount(String(Math.max(rawSalary - deduction, 0)));
    }
  }, [salary, advanceDeduction]);

  useEffect(() => {
    if (!selectedCurrency || !selectedShop) {
      setSelectedAccountId("");
      return;
    }
    if (accounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = accounts.some((acc) => String(acc.id) === String(selectedAccountId));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || accounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [selectedCurrency, selectedShop, accounts, sameCurrencyAccounts, selectedAccountId]);

  const { data: directRateData } = useFetchObjects(
    ["payroll-edit-rate-direct", selectedShop, selectedCurrency, selectedAccount?.currency],
    selectedShop && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShop}&from_currency=${selectedCurrency}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["payroll-edit-rate-reverse", selectedShop, selectedCurrency, selectedAccount?.currency],
    selectedShop && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShop}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrency}`
      : "",
    token
  );
  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrency) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrency;
    if (String(selectedCurrency) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrency, selectedAccount, directRateData, reverseRateData]);
  useEffect(() => {
    if (!editData || !exchangeRatePreview) return;
    const currentRateSelection = `${selectedCurrency}:${selectedAccountId}`;
    if (currentRateSelection !== initialRateSelectionRef.current) {
      setExchangeRate(exchangeRatePreview);
    }
  }, [editData, exchangeRatePreview, selectedCurrency, selectedAccountId]);
  const handleSubmit = () => {
    const deduction = Number(advanceDeduction || 0);
    const remainingAdvanceBalance = Math.max(previousRemainingAdvance - deduction, 0);
    const computedAdvanceStatus =
      deduction > 0 && remainingAdvanceBalance === 0
        ? "Fully Deducted"
        : deduction > 0
        ? "Partially Deducted"
        : previousRemainingAdvance > 0
        ? "Pending"
        : "No Advance";
    const formData = new FormData();
    formData.append("shop", selectedShop);
    formData.append("employee", selectedEmployee);
    formData.append("currency", selectedCurrency);
    formData.append("account", selectedAccountId);
    formData.append("amount", amount);
    formData.append("exchange_rate", exchangeRate);
    if (monthYear) {
      const [year, month] = monthYear.split("-");
      formData.append("year", year);
      formData.append("month", month);
    }
    formData.append("payment_date", paymentDate);
    formData.append("previous_advance", String(previousAdvance));
    formData.append("advance_deduction", String(deduction));
    formData.append("remaining_advance_balance", String(remainingAdvanceBalance));
    formData.append("advance_status", computedAdvanceStatus);
    formData.append("notes", notes);
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setSelectedShop("");
      setSelectedEmployee("");
      setSelectedCurrency("");
      setSelectedAccountId("");
      setAmount("");
      setExchangeRate("");
      setSalary("");
      setMonthYear("");
      setPaymentDate("");
      setNotes("");
      setAdvanceDeduction("");
      onSaved?.();
      onClose();
    }
  }, [isSuccess, onClose, onSaved]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("payroll.editPayroll")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          <TextField
            select
            margin="dense"
            label={t("payroll.selectShop")}
            fullWidth
            required
            value={safeSelectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            <MenuItem value="">{t("payroll.selectShop")}</MenuItem>
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (shops || []).map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("payroll.selectEmployee")}
            fullWidth
            required
            value={safeSelectedEmployee}
            onChange={(e) => {
              paymentEditedRef.current = false;
              setSelectedEmployee(e.target.value);
            }}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            <MenuItem value="">{t("payroll.selectEmployee")}</MenuItem>
            {employeeLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              employeeOptions.map((emp) => (
                <MenuItem key={emp.id} value={String(emp.id)}>
                  {emp.name || "--"}
                </MenuItem>
              ))
            )}
          </TextField>

          <JalaliDateField mode="month"
            margin="dense"
            label={t("payroll.monthYear", "Month/Year")}
            fullWidth
            required
            value={monthYear}
            onChange={(e) => {
              paymentEditedRef.current = false;
              setMonthYear(e.target.value);
            }}
            {...getRtlTextFieldProps(formDirection, {
              InputLabelProps: { shrink: true },
            })}
          />

          <JalaliDateField mode="datetime"
            margin="dense"
            label={t("payroll.paymentDate")}
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            {...getRtlTextFieldProps(formDirection, {
              InputLabelProps: { shrink: true },
            })}
          />

          {selectedEmployee && (
            <TextField
              margin="dense"
              label={t("payroll.employeeSalary")}
              type="text"
              fullWidth
              value={salary || "--"}
              InputProps={{
                readOnly: true,
              }}
              {...getRtlTextFieldProps(formDirection)}
            />
          )}
          <TextField
            select
            margin="dense"
            label={t("payroll.selectCurrency")}
            fullWidth
            required
            value={safeSelectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            <MenuItem value="">{t("payroll.selectCurrency")}</MenuItem>
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (currencies || []).map((cur) => (
                <MenuItem key={cur.id} value={String(cur.id)}>
                  {cur.title || "--"}
                </MenuItem>
              ))
            )}
          </TextField>
          {selectedEmployee && (
            <>
              <TextField
                margin="dense"
                label={t("payroll.advanceAmount", "Advance Amount")}
                fullWidth
                value={Number(advanceDeduction || 0).toFixed(2)}
                InputProps={{ readOnly: true }}
                {...getRtlTextFieldProps(formDirection)}
              />
              <TextField
                margin="dense"
                label={t("payroll.netSalary", "Net Salary")}
                fullWidth
                required
                value={Number(netSalary || 0).toFixed(2)}
                InputProps={{ readOnly: true }}
                {...getRtlTextFieldProps(formDirection)}
              />
              <TextField
                margin="dense"
                label={t("payroll.paymentAmount", "Payment Amount")}
                type="number"
                fullWidth
                value={amount}
                onChange={(e) => {
                  paymentEditedRef.current = true;
                  setAmount(e.target.value);
                }}
                {...getRtlTextFieldProps(formDirection, {
                  inputProps: { min: 0, max: netSalary, step: "0.01" },
                })}
              />
              <TextField
                margin="dense"
                label={t("payroll.remainingPayment", "Remaining Payment")}
                fullWidth
                value={remainingPaymentBalance.toFixed(2)}
                InputProps={{ readOnly: true }}
                {...getRtlTextFieldProps(formDirection)}
              />
              <TextField
                margin="dense"
                label={t("payroll.paymentStatus", "Payment Status")}
                fullWidth
                value={paymentStatus}
                InputProps={{ readOnly: true }}
                {...getRtlTextFieldProps(formDirection)}
              />
            </>
          )}
          <TextField
            select
            margin="dense"
            label={t("payroll.account", "Account")}
            fullWidth
            required
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!selectedShop || !selectedCurrency || accountLoading}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : accounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("payroll.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              accounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            margin="dense"
            label={t("payroll.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            {...getRtlTextFieldProps(formDirection, {
              inputProps: { min: 0, step: "0.000001" },
            })}
          />
          {!exchangeRatePreview && selectedCurrency && selectedAccountId && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1, direction: fieldDirection, textAlign: fieldTextAlign }}>
              {t("payroll.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
            </Alert>
          )}
          <TextField
            margin="dense"
            label={t("payroll.notes")}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("payroll.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !selectedEmployee ||
            !selectedCurrency ||
            amount === "" ||
            !selectedAccountId ||
            !exchangeRate ||
            !monthYear ||
            !paymentDate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("payroll.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayrollEdit;
